import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Download, RefreshCw, Save, Trash2 } from "lucide-react";
import {
  clientInventoryClients, clientInventoryUrls, clientInventoryCapture,
  clientInventoryMap, clientInventoryClear, clientInventoryCsvUrl, getToken,
} from "@/lib/api";
import { toast } from "sonner";

/** Non-technical redirect-map editor. Loads captured URLs for a client,
 *  lets the operator override the auto-generated /new-url target, and
 *  downloads the final CSV for 301 upload. */

const STATUS_MAP = {
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  sky: "bg-sky-100 text-sky-800 border-sky-200",
  rose: "bg-rose-100 text-rose-800 border-rose-200",
  slate: "bg-slate-100 text-slate-800 border-slate-200",
};
const StatusBadge = ({ code }) => {
  if (!code) return <Badge variant="outline">—</Badge>;
  const c = code >= 200 && code < 300 ? "emerald" : code >= 300 && code < 400 ? "sky" : code >= 400 ? "rose" : "slate";
  return <Badge className={STATUS_MAP[c]}>{code}</Badge>;
};

export default function AdminClientInventory() {
  const { client } = useParams();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [editing, setEditing] = useState({});   // rowId -> new url
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        clientInventoryClients(),
        clientInventoryUrls(client),
      ]);
      const m = (c.clients || []).find(x => x.key === client);
      setMeta(m || null);
      setRows(u.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load inventory");
    } finally { setLoading(false); }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const capture = async (force) => {
    setCapturing(true);
    try {
      const r = await clientInventoryCapture(client, { limit: 250, force_refresh: !!force });
      if (r.cached) toast.info(`Using cached capture (${r.url_count} URLs)`);
      else toast.success(`Captured ${r.url_count} URLs via ${r.source}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Capture failed");
    } finally { setCapturing(false); }
  };

  const saveMap = async (rowId) => {
    const v = (editing[rowId] || "").trim();
    if (!v) return toast.error("Enter a target URL");
    try {
      await clientInventoryMap(client, rowId, { redirect_target: v });
      toast.success("Redirect target saved");
      setEditing((p) => { const n = { ...p }; delete n[rowId]; return n; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete all captured URLs for ${client}?`)) return;
    try {
      await clientInventoryClear(client);
      toast.success("Cleared");
      await load();
    } catch (e) { toast.error("Clear failed"); }
  };

  const filtered = rows.filter(r => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.requested_url || "").toLowerCase().includes(s)
      || (r.title || "").toLowerCase().includes(s)
      || (r.redirect_target || "").toLowerCase().includes(s);
  });

  const derivedTarget = (r) => {
    if (r.redirect_target) return r.redirect_target;
    if (!meta) return "";
    try {
      const path = new URL(r.final_url || r.requested_url).pathname || "/";
      return meta.replacement_base.replace(/\/$/, "") + path;
    } catch { return ""; }
  };

  const downloadCsv = () => {
    const token = getToken();
    if (!token) return toast.error("Sign in as admin first");
    fetch(clientInventoryCsvUrl(client), {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async r => {
      if (!r.ok) throw new Error("CSV failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${client}-redirect-map.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Download failed"));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-client-inventory">
      <Navbar />
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8">
          <Link to="/admin" className="text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-orange-600">← Admin</Link>
          <h1 className="mt-2 font-heading text-3xl font-black text-slate-900">
            {meta?.name || client} · 301 Redirect Map
          </h1>
          {meta && (
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
              <span>Source: <b className="font-mono">{meta.root_url}</b></span>
              <span>Target base: <b className="font-mono">{meta.replacement_base}</b></span>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="ci-capture" onClick={() => capture(false)} disabled={capturing}>
              {capturing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
              Capture URLs
            </Button>
            <Button variant="outline" data-testid="ci-force" onClick={() => capture(true)} disabled={capturing}>
              Force re-crawl
            </Button>
            <Button variant="outline" data-testid="ci-csv" onClick={downloadCsv}>
              <Download className="mr-1 h-4 w-4" /> Download CSV
            </Button>
            <Button variant="ghost" className="text-rose-600" data-testid="ci-clear" onClick={clearAll}>
              <Trash2 className="mr-1 h-4 w-4" /> Clear
            </Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {loading ? "Loading…" : `${filtered.length} of ${rows.length} URLs`}
          </div>
          <Input
            data-testid="ci-search"
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Filter by URL, title, target…"
            className="max-w-xs bg-white"
          />
        </div>

        <Card className="border-slate-200 bg-white p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Source URL</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">→ New URL (301 target)</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const rid = r._id;
                  const editVal = editing[rid];
                  const cur = editVal ?? derivedTarget(r);
                  const isCustom = !!r.redirect_target;
                  return (
                    <tr key={rid} className="border-t border-slate-100" data-testid={`ci-row-${rid}`}>
                      <td className="px-4 py-3 align-top">
                        <a href={r.requested_url} target="_blank" rel="noreferrer" className="break-all font-mono text-xs text-slate-800 hover:text-orange-600">
                          {r.requested_url}
                        </a>
                      </td>
                      <td className="px-4 py-3 align-top"><StatusBadge code={r.status_code} /></td>
                      <td className="px-4 py-3 align-top text-xs text-slate-700">
                        <div className="line-clamp-2">{r.title || <span className="italic text-slate-400">—</span>}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Input
                          data-testid={`ci-target-${rid}`}
                          value={cur}
                          onChange={e => setEditing({ ...editing, [rid]: e.target.value })}
                          className="h-8 font-mono text-xs"
                        />
                        {isCustom && !editVal && (
                          <div className="mt-1 text-[10px] uppercase tracking-wider text-emerald-600">Custom</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => saveMap(rid)}
                          disabled={!editVal || editVal === (r.redirect_target || "")}
                          data-testid={`ci-save-${rid}`}
                        >
                          <Save className="mr-1 h-3.5 w-3.5" /> Save
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400">
                    {rows.length === 0 ? "No URLs captured yet. Click Capture URLs." : "Nothing matches your filter."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </section>
      <Footer />
    </div>
  );
}
